import { CodeGenStatus, CodeGenStatusType } from '@common/enums';
import { Dialog, Stack, Text, theme } from '@prophecy/ui';
import React from 'react';

export const isCodeGenerationInProgress = (status?: string | null) => {
  return status === CodeGenStatus.InProgress || status === CodeGenStatus.InQueue;
};

const getCodeGenerationMessage = (inQueueCount = 0, inProgressCount = 0) => {
  return (
    <Stack>
      Project’s code is being updated.
      <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
        {inProgressCount > 0 && <span>In Progress: {inProgressCount}</span>}
        {inQueueCount > 0 && <span>Queued: {inQueueCount}</span>}
      </Stack>
      Please wait until the updates are complete.
    </Stack>
  );
};

const CODE_GENERATION_MESSAGE = (inQueueCount = 0, inProgressCount = 0) => ({
  [CodeGenStatus.InProgress]: getCodeGenerationMessage(inQueueCount, inProgressCount),
  [CodeGenStatus.InQueue]: getCodeGenerationMessage(inQueueCount, inProgressCount),
  [CodeGenStatus.Default]: ''
});

export const getRefreshingMessage = ({
  status,
  refreshing,
  title = '',
  inQueueCount = 0,
  inProgressCount = 0
}: {
  status?: CodeGenStatusType;
  refreshing?: boolean;
  title?: string;
  inQueueCount?: number;
  inProgressCount?: number;
}) => {
  const message = CODE_GENERATION_MESSAGE(inQueueCount, inProgressCount)[
    refreshing ? CodeGenStatus.InProgress : status || CodeGenStatus.InProgress
  ];
  return refreshing || (status && status !== CodeGenStatus.Default) ? message : title;
};

export const getRebuildGenerationHeading = (
  unCommittedCount: number,
  codeGenWarning: string | React.ReactNode = 'Updating the dependency will regenerate code for all pipelines and jobs'
) => {
  return unCommittedCount > 0 && typeof codeGenWarning === 'string'
    ? `You have uncommitted changes in your Pipelines. ${codeGenWarning}. Consider saving your changes with a commit for easier rollback.`
    : codeGenWarning;
};

export const getCloseButtonProps = (hasUnCommittedFiles: number, onClick: () => void, disabledLostText = false) => ({
  children: hasUnCommittedFiles ? 'Commit & Save' : 'Cancel',
  onClick,
  tooltip: hasUnCommittedFiles
    ? `${disabledLostText ? '' : 'Your changes will be lost.'} On click we will open commit screen.`
    : undefined
});

export const getCodeGenWarningDialog = async ({
  saveBtn = 'Proceed',
  warningText = '',
  codeGenWarning,
  title = `Confirm Your Action`,
  hasRebuildWarning = true,
  isPipelineIDE,
  onCloseClick,
  unCommittedCount = 0,
  disabledLostText
}: {
  saveBtn?: string;
  isPipelineIDE?: boolean;
  unCommittedCount?: number;
  warningText?: string;
  title?: string | React.ReactNode;
  disabledLostText?: boolean;
  onCloseClick?: () => void;
  codeGenWarning?: string | React.ReactNode;
  hasRebuildWarning?: boolean;
}) => {
  const hasUnCommittedFiles = unCommittedCount > 0;
  let description = getRebuildGenerationHeading(unCommittedCount, codeGenWarning);
  description = isPipelineIDE
    ? `${description} Dependencies change might also require a cluster restart if there are missing dependencies.`
    : description;
  const dialogResult = await Dialog.warning({
    'data-test-id': 'code-gen-warning-dialog',
    title,
    children: (
      <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x16} 0`}>
        {warningText && <Text level='sm'>{warningText}</Text>}
        {hasRebuildWarning && <Text level='sm'>{description}</Text>}
      </Stack>
    ),
    size: 'l',
    closeButton: getCloseButtonProps(unCommittedCount, () => onCloseClick?.(), disabledLostText),
    okButton: {
      children: hasUnCommittedFiles ? 'Save Without Committing' : saveBtn,
      block: false
    }
  });
  return dialogResult === Dialog.ResponseAction.okButton;
};
